/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/aiAssistantPersistence.ts
 * PURPOSE: Persist Langflow assistant threads per browser tab (sessionStorage), scoped by tenant+user
 *          and flow. Supports multiple threads per scope via a history index + per-thread payloads.
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~300 / 300 (utility module; advisory vs component budget)
 * RECENT: 2026-05-09 [2.2.901] **`safeRandomUUID`** — RFC4122-shaped ids when **`crypto.randomUUID`** is missing (**`getRandomValues`** or **`Math.random`**); **`createThreadId`** delegates; **`AiAssistantPanel`** uses **`safeRandomUUID`** for message or assistant ids; Vitest **`aiAssistantPersistence.test.ts`**; Playwright **`assistant.spec.ts`** **`Langflow slide-out mounts when crypto.randomUUID is disabled [2.2.901]`**; root **`CHANGELOG` [2.2.901]`**; **`make dev`** unchanged.
 */

import type { AiAssistantMessage } from './aiAssistantTypes';

const PREFIX_V3 = 'brazn:ai-assistant:v3';
const PREFIX_V2 = 'brazn:ai-assistant:v2';
const MAX_HISTORY = 30;

/** Safe segment for storage keys (sessionScope may contain `:` from `tenant_id:user_id`). */
function scopeKeySegment(sessionScope: string): string {
  const enc = encodeURIComponent(sessionScope);
  return enc.length > 180 ? `${enc.slice(0, 180)}…` : enc;
}

function historyIndexKey(sessionScope: string, flowId: string): string {
  return `${PREFIX_V3}:hidx:${scopeKeySegment(sessionScope)}:${flowId || 'unset'}`;
}

function threadBodyKey(sessionScope: string, flowId: string, threadId: string): string {
  return `${PREFIX_V3}:thr:${scopeKeySegment(sessionScope)}:${flowId || 'unset'}:${threadId}`;
}

/** v2 single-thread key — migrated to v3 on first read */
function v2LegacyKey(sessionScope: string, flowId: string): string {
  return `${PREFIX_V2}:${scopeKeySegment(sessionScope)}:${flowId || 'unset'}`;
}

export function createSessionId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * UUID v4 when `crypto.randomUUID` exists (secure contexts); otherwise RFC4122-shaped id from
 * `getRandomValues` or `Math.random` — avoids TypeError in HTTP / embedded hosts (e.g. some IDE browsers).
 */
export function safeRandomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createThreadId(): string {
  return safeRandomUUID();
}

export interface ThreadHistoryEntry {
  threadId: string;
  preview: string;
  updatedAt: string;
}

export interface ThreadHistoryIndex {
  activeThreadId: string;
  entries: ThreadHistoryEntry[];
}

function parseMessages(
  rawMessages: Array<Omit<AiAssistantMessage, 'timestamp'> & { timestamp: string }> | undefined
): AiAssistantMessage[] {
  if (!Array.isArray(rawMessages)) return [];
  return rawMessages
    .filter((m) => !m.loading)
    .map((m) => ({
      ...m,
      timestamp: new Date(m.timestamp),
    }));
}

export function loadHistoryIndex(
  sessionScope: string,
  flowId: string
): ThreadHistoryIndex | null {
  if (typeof window === 'undefined' || !flowId.trim() || !sessionScope.trim()) return null;
  try {
    const raw = sessionStorage.getItem(historyIndexKey(sessionScope, flowId));
    if (!raw) return null;
    const data = JSON.parse(raw) as {
      activeThreadId?: string;
      entries?: ThreadHistoryEntry[];
    };
    if (!data.activeThreadId || !Array.isArray(data.entries)) return null;
    return { activeThreadId: data.activeThreadId, entries: data.entries };
  } catch {
    return null;
  }
}

function saveHistoryIndex(sessionScope: string, flowId: string, index: ThreadHistoryIndex): void {
  sessionStorage.setItem(historyIndexKey(sessionScope, flowId), JSON.stringify(index));
}

function previewFromUserContent(content: string): string {
  const t = content.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.length > 72 ? `${t.slice(0, 72)}…` : t;
}

function migrateV2IfNeeded(sessionScope: string, flowId: string): void {
  if (typeof window === 'undefined') return;
  if (loadHistoryIndex(sessionScope, flowId)) return;
  const legacyRaw = sessionStorage.getItem(v2LegacyKey(sessionScope, flowId));
  if (!legacyRaw) return;
  try {
    const data = JSON.parse(legacyRaw) as {
      sessionScope?: string;
      flowId?: string;
      sessionId?: string;
      messages?: Array<Omit<AiAssistantMessage, 'timestamp'> & { timestamp: string }>;
    };
    if (data.sessionScope !== sessionScope || data.flowId !== flowId) return;
    if (!data.sessionId || !Array.isArray(data.messages)) return;
    const messages = parseMessages(data.messages);
    if (messages.length === 0) {
      sessionStorage.removeItem(v2LegacyKey(sessionScope, flowId));
      return;
    }
    const threadId = createThreadId();
    const firstUser = messages.find((m) => m.role === 'user');
    const preview = previewFromUserContent(firstUser?.content ?? '') || 'Imported chat';
    const updatedAt = new Date().toISOString();
    sessionStorage.setItem(
      threadBodyKey(sessionScope, flowId, threadId),
      JSON.stringify({
        threadId,
        sessionScope,
        flowId,
        sessionId: data.sessionId,
        messages: messages.map((m) => ({
          ...m,
          timestamp: m.timestamp.toISOString(),
        })),
      })
    );
    saveHistoryIndex(sessionScope, flowId, {
      activeThreadId: threadId,
      entries: [{ threadId, preview, updatedAt }],
    });
    sessionStorage.removeItem(v2LegacyKey(sessionScope, flowId));
  } catch {
    /* ignore corrupt legacy */
  }
}

export function loadThreadBody(
  sessionScope: string,
  flowId: string,
  threadId: string
): { sessionId: string; messages: AiAssistantMessage[] } | null {
  if (typeof window === 'undefined' || !flowId.trim() || !sessionScope.trim() || !threadId) return null;
  try {
    const raw = sessionStorage.getItem(threadBodyKey(sessionScope, flowId, threadId));
    if (!raw) return null;
    const data = JSON.parse(raw) as {
      threadId?: string;
      sessionScope?: string;
      flowId?: string;
      sessionId?: string;
      messages?: Array<Omit<AiAssistantMessage, 'timestamp'> & { timestamp: string }>;
    };
    if (data.threadId !== threadId || data.sessionScope !== sessionScope || data.flowId !== flowId)
      return null;
    if (!data.sessionId || !Array.isArray(data.messages)) return null;
    return { sessionId: data.sessionId, messages: parseMessages(data.messages) };
  } catch {
    return null;
  }
}

export interface AssistantSessionState {
  activeThreadId: string;
  sessionId: string;
  messages: AiAssistantMessage[];
  historyEntries: ThreadHistoryEntry[];
}

/** Bootstrap index (and migrate v2), then load the active thread body. */
export function loadAssistantSession(
  sessionScope: string,
  flowId: string
): AssistantSessionState {
  if (typeof window === 'undefined' || !flowId.trim() || !sessionScope.trim()) {
    return {
      activeThreadId: createThreadId(),
      sessionId: createSessionId(),
      messages: [],
      historyEntries: [],
    };
  }
  migrateV2IfNeeded(sessionScope, flowId);
  let idx = loadHistoryIndex(sessionScope, flowId);
  if (!idx) {
    const activeThreadId = createThreadId();
    idx = { activeThreadId, entries: [] };
    saveHistoryIndex(sessionScope, flowId, idx);
  }
  const body = loadThreadBody(sessionScope, flowId, idx.activeThreadId);
  if (body) {
    return {
      activeThreadId: idx.activeThreadId,
      sessionId: body.sessionId,
      messages: body.messages,
      historyEntries: idx.entries,
    };
  }
  return {
    activeThreadId: idx.activeThreadId,
    sessionId: createSessionId(),
    messages: [],
    historyEntries: idx.entries,
  };
}

export interface PersistThreadResult {
  activeThreadId: string;
  historyEntries: ThreadHistoryEntry[];
}

/** Writes thread body and updates the history index (preview, sort, cap). */
export function persistThread(
  sessionScope: string,
  flowId: string,
  threadId: string,
  sessionId: string,
  messages: AiAssistantMessage[]
): PersistThreadResult {
  if (typeof window === 'undefined' || !flowId.trim() || !sessionScope.trim()) {
    return { activeThreadId: threadId, historyEntries: [] };
  }
  const filtered = messages.filter((m) => !m.loading);
  const idx =
    loadHistoryIndex(sessionScope, flowId) ??
    ({ activeThreadId: threadId, entries: [] as ThreadHistoryEntry[] } satisfies ThreadHistoryIndex);

  if (filtered.length === 0) {
    sessionStorage.removeItem(threadBodyKey(sessionScope, flowId, threadId));
    idx.entries = idx.entries.filter((e) => e.threadId !== threadId);
    if (idx.activeThreadId === threadId) {
      idx.activeThreadId = idx.entries[0]?.threadId ?? createThreadId();
    }
    saveHistoryIndex(sessionScope, flowId, idx);
    return { activeThreadId: idx.activeThreadId, historyEntries: idx.entries };
  }

  const firstUser = filtered.find((m) => m.role === 'user');
  const preview = previewFromUserContent(firstUser?.content ?? '') || 'Chat';
  const updatedAt = new Date().toISOString();
  sessionStorage.setItem(
    threadBodyKey(sessionScope, flowId, threadId),
    JSON.stringify({
      threadId,
      sessionScope,
      flowId,
      sessionId,
      messages: filtered.map((m) => ({
        ...m,
        timestamp: m.timestamp.toISOString(),
      })),
    })
  );

  const without = idx.entries.filter((e) => e.threadId !== threadId);
  idx.entries = [{ threadId, preview, updatedAt }, ...without].slice(0, MAX_HISTORY);
  idx.activeThreadId = threadId;
  saveHistoryIndex(sessionScope, flowId, idx);
  return { activeThreadId: threadId, historyEntries: idx.entries };
}

export function setActiveThreadInIndex(
  sessionScope: string,
  flowId: string,
  threadId: string
): ThreadHistoryIndex {
  if (typeof window === 'undefined' || !flowId.trim() || !sessionScope.trim()) {
    return { activeThreadId: threadId, entries: [] };
  }
  const idx = loadHistoryIndex(sessionScope, flowId) ?? {
    activeThreadId: threadId,
    entries: [] as ThreadHistoryEntry[],
  };
  idx.activeThreadId = threadId;
  saveHistoryIndex(sessionScope, flowId, idx);
  return idx;
}
